import {
  KnockOutRuleSchema,
  ReviewAlertSchema,
  type DecisionPolicy,
  type KnockOutRule,
  type ReviewAlert,
} from '@paguz/contracts';
import { Badge, cn, MoneyText } from '@paguz/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { SparklesIcon } from 'lucide-react';
import { useDeferredValue } from 'react';
import { api } from '../../api/endpoints.ts';
import { Checkbox, Field, Select, Switch } from '../../components/form-controls.tsx';
import { Panel } from '../../components/page.tsx';
import { KNOCK_OUT, REVIEW_ALERT } from '../../lib/labels.ts';
import { decisionPolicyErrors } from './validation.ts';
import { FormCard, IntField, MoneyField, SettingSection } from './setting-editor.tsx';

function toggle<T>(list: readonly T[], item: T, on: boolean): T[] {
  return on ? [...new Set([...list, item])] : list.filter((x) => x !== item);
}

// ── Política de decisión (autoaprobación) ─────────────────────────────────
const DECISION_LABELS: Readonly<Record<string, string>> = {
  'autoApproval.enabled': 'Autoaprobación activa',
  'autoApproval.maxLimit': 'Cupo máximo autoaprobable',
  'autoApproval.minScore': 'Puntaje mínimo',
  'autoApproval.requireNoAlerts': 'Exigir cero alertas',
  'autoRejection.enabled': 'Autorrechazo por knock-outs',
  'autoRejection.knockOuts': 'Knock-outs de autorrechazo',
  forceReviewAlerts: 'Alertas que fuerzan revisión',
  offerTtlDays: 'Vigencia de la oferta',
};

export function DecisionPolicySection() {
  return (
    <SettingSection
      settingKey="decisionPolicy"
      title="Política de decisión"
      description="Qué expedientes se aprueban solos y cuáles pasan al analista. La aplica el motor de decisión (código determinístico); la IA nunca aprueba créditos."
      labels={DECISION_LABELS}
      aside={(editor) => <DecisionPreview policy={editor.draft} />}
    >
      {({ draft, set, error }) => (
        <>
          <FormCard
            title="Autoaprobación"
            description="Por debajo del umbral y sin alertas, el expediente se aprueba sin intervención humana."
          >
            <Switch
              label="Autoaprobación activa"
              description="Si la apagas, todos los expedientes pasan por un analista."
              checked={draft.autoApproval.enabled}
              onCheckedChange={(v) => {
                set(['autoApproval', 'enabled'], v);
              }}
            />
            <div
              className={cn(
                'grid gap-5 md:grid-cols-2',
                !draft.autoApproval.enabled && 'opacity-50',
              )}
            >
              <MoneyField
                label="Cupo máximo autoaprobable"
                value={draft.autoApproval.maxLimit}
                onChange={(v) => {
                  set(['autoApproval', 'maxLimit'], v);
                }}
                error={error(['autoApproval', 'maxLimit'])}
                hint="Por encima, decide el analista (p. ej. Jairo: $ 4.000.000)."
              />
              <IntField
                label="Puntaje mínimo"
                suffix="/ 1000"
                value={draft.autoApproval.minScore}
                onChange={(v) => {
                  set(['autoApproval', 'minScore'], v);
                }}
                error={error(['autoApproval', 'minScore'])}
                hint="Scorecard de originación, escala 0–1000."
              />
            </div>
            <Switch
              label="Exigir cero alertas documentales y de listas"
              checked={draft.autoApproval.requireNoAlerts}
              onCheckedChange={(v) => {
                set(['autoApproval', 'requireNoAlerts'], v);
              }}
            />
          </FormCard>

          <FormCard
            title="Autorrechazo"
            description="Los knock-outs marcados rechazan el expediente sin revisión (con motivo codificado)."
          >
            <Switch
              label="Autorrechazo por knock-outs"
              checked={draft.autoRejection.enabled}
              onCheckedChange={(v) => {
                set(['autoRejection', 'enabled'], v);
              }}
            />
            <div
              className={cn(
                'grid gap-2 md:grid-cols-2',
                !draft.autoRejection.enabled && 'opacity-50',
              )}
            >
              {KnockOutRuleSchema.options.map((rule: KnockOutRule) => (
                <Checkbox
                  key={rule}
                  label={KNOCK_OUT[rule]}
                  description={<code className="font-mono text-xs">{rule}</code>}
                  checked={draft.autoRejection.knockOuts.includes(rule)}
                  onCheckedChange={(on) => {
                    set(
                      ['autoRejection', 'knockOuts'],
                      toggle(draft.autoRejection.knockOuts, rule, on),
                    );
                  }}
                />
              ))}
            </div>
            {error(['autoRejection', 'knockOuts']) && (
              <p role="alert" className="text-sm font-medium text-destructive">
                {error(['autoRejection', 'knockOuts'])}
              </p>
            )}
          </FormCard>

          <FormCard
            title="Alertas que fuerzan revisión humana"
            description="Aunque se cumpla todo lo demás, estas alertas envían el expediente al analista."
          >
            <div className="grid gap-2 md:grid-cols-2">
              {ReviewAlertSchema.options.map((alert: ReviewAlert) => (
                <Checkbox
                  key={alert}
                  label={REVIEW_ALERT[alert]}
                  description={<code className="font-mono text-xs">{alert}</code>}
                  checked={draft.forceReviewAlerts.includes(alert)}
                  onCheckedChange={(on) => {
                    set(['forceReviewAlerts'], toggle(draft.forceReviewAlerts, alert, on));
                  }}
                />
              ))}
            </div>
          </FormCard>

          <FormCard title="Oferta">
            <IntField
              label="Vigencia de la oferta presentada al cliente"
              suffix="días"
              value={draft.offerTtlDays}
              onChange={(v) => {
                set(['offerTtlDays'], v);
              }}
              error={error(['offerTtlDays'])}
            />
          </FormCard>
        </>
      )}
    </SettingSection>
  );
}

const OUTCOME = {
  AUTO_APPROVE: { label: 'Autoaprobado', tone: 'success', bar: 'bg-success' },
  REVIEW: { label: 'Analista', tone: 'warning', bar: 'bg-warning' },
  AUTO_REJECT: { label: 'Autorrechazado', tone: 'danger', bar: 'bg-destructive' },
} as const;

function DecisionPreview({ policy }: { policy: DecisionPolicy }) {
  const deferred = useDeferredValue(policy);
  const valid = Object.keys(decisionPolicyErrors(deferred)).length === 0;
  const preview = useQuery({
    queryKey: ['decision-preview', deferred],
    queryFn: () => api.previewDecisionPolicy(deferred),
    enabled: valid,
    placeholderData: keepPreviousData,
  });
  const data = preview.data;
  return (
    <Panel
      title={
        <span className="flex items-center gap-2">
          <SparklesIcon className="size-5 text-highlight" /> Vista previa
        </span>
      }
      description="Simulación con el borrador, sin guardar."
      className="2xl:sticky 2xl:top-24"
    >
      {!data ? (
        <p className="text-sm text-muted-foreground">Calculando…</p>
      ) : (
        <div className={cn('flex flex-col gap-4', preview.isFetching && 'opacity-70')}>
          <p className="text-[0.9375rem] leading-relaxed">
            Con esta política,{' '}
            <strong className="font-display text-2xl text-primary">{data.autoApproved}</strong> de
            los últimos <strong>{data.total}</strong> expedientes ({data.windowLabel}) se habrían{' '}
            <strong>autoaprobado</strong>.
          </p>
          <div className="flex h-3 overflow-hidden rounded-full bg-muted" aria-hidden="true">
            {(['AUTO_APPROVE', 'REVIEW', 'AUTO_REJECT'] as const).map((o) => {
              const n =
                o === 'AUTO_APPROVE'
                  ? data.autoApproved
                  : o === 'REVIEW'
                    ? data.toReview
                    : data.autoRejected;
              return (
                <span
                  key={o}
                  className={OUTCOME[o].bar}
                  style={{ width: `${(n / Math.max(data.total, 1)) * 100}%` }}
                />
              );
            })}
          </div>
          <dl className="grid grid-cols-3 gap-2 text-center text-sm">
            <div>
              <dt className="text-muted-foreground">Autoaprobados</dt>
              <dd className="font-display text-xl font-semibold text-success">
                {data.autoApproved}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Al analista</dt>
              <dd className="font-display text-xl font-semibold text-warning">{data.toReview}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Autorrechazados</dt>
              <dd className="font-display text-xl font-semibold text-destructive">
                {data.autoRejected}
              </dd>
            </div>
          </dl>
          <ul className="flex flex-col divide-y divide-border rounded-xl border border-border text-sm">
            {data.samples.map((s) => (
              <li
                key={s.applicationId}
                className={cn(
                  'flex items-start justify-between gap-3 px-3 py-2',
                  s.applicationId === 'app_jairo' && 'bg-highlight-soft/50',
                )}
              >
                <span className="min-w-0">
                  <span className="block font-medium">{s.applicant}</span>
                  <span className="block text-xs text-muted-foreground">
                    <MoneyText value={s.suggestedLimit.amount} size="sm" className="text-xs" /> ·
                    puntaje {s.score} · {s.why}
                  </span>
                </span>
                <Badge variant={OUTCOME[s.outcome].tone}>{OUTCOME[s.outcome].label}</Badge>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}

// ── Reversión y disputas ──────────────────────────────────────────────────
const REVERSAL_LABELS: Readonly<Record<string, string>> = {
  'reversalPolicy.claimWindowDays': 'Plazo máximo para reclamar',
  'reversalPolicy.refundInterest': 'Reversar intereses',
  'reversalPolicy.refundFees': 'Reversar comisiones',
  'reversalPolicy.refundInsurance': 'Reversar seguro',
  'reversalPolicy.creditBalanceTreatment': 'Saldo a favor',
  'disputePolicy.freezeAccrual': 'Congelar intereses en disputa',
  'disputePolicy.excludeFromMinimum': 'Excluir del pago mínimo',
  'disputePolicy.suspendDpd': 'No reportar mora en disputa',
  'disputePolicy.maxDays': 'Duración máxima de la disputa',
  'disputePolicy.decisionDueBusinessDays': 'Plazo para decidir',
};

export function ReversalSection() {
  return (
    <SettingSection
      settingKey="reversal"
      title="Reversión y disputas"
      description="Qué se le devuelve al cliente cuando una compra se reversa (devolución del comercio o disputa aceptada) y qué pasa mientras la disputa está abierta."
      labels={REVERSAL_LABELS}
    >
      {({ draft, set, error }) => (
        <>
          <FormCard
            title="Política de reversión"
            description="Aplica a devoluciones del comercio y disputas aceptadas."
          >
            <IntField
              label="Plazo máximo para reclamar"
              suffix="días"
              value={draft.reversalPolicy.claimWindowDays}
              onChange={(v) => {
                set(['reversalPolicy', 'claimWindowDays'], v);
              }}
              error={error(['reversalPolicy', 'claimWindowDays'])}
              hint="Desde la fecha de la compra."
            />
            <div className="grid gap-4 md:grid-cols-2">
              <Switch
                label="Reversar intereses causados"
                checked={draft.reversalPolicy.refundInterest}
                onCheckedChange={(v) => {
                  set(['reversalPolicy', 'refundInterest'], v);
                }}
              />
              <Switch
                label="Reversar seguro de vida"
                checked={draft.reversalPolicy.refundInsurance}
                onCheckedChange={(v) => {
                  set(['reversalPolicy', 'refundInsurance'], v);
                }}
              />
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Comisiones" htmlFor="rev-fees">
                <Select
                  id="rev-fees"
                  value={draft.reversalPolicy.refundFees}
                  onChange={(e) => {
                    set(['reversalPolicy', 'refundFees'], e.target.value);
                  }}
                >
                  <option value="NONE">No se reversan</option>
                  <option value="PROPORTIONAL">Proporcional al monto reversado</option>
                  <option value="FULL">Se reversan completas</option>
                </Select>
              </Field>
              <Field label="Saldo a favor resultante" htmlFor="rev-balance">
                <Select
                  id="rev-balance"
                  value={draft.reversalPolicy.creditBalanceTreatment}
                  onChange={(e) => {
                    set(['reversalPolicy', 'creditBalanceTreatment'], e.target.value);
                  }}
                >
                  <option value="KEEP_AS_CREDIT_BALANCE">
                    Queda como saldo a favor en el cupo
                  </option>
                  <option value="RETURN_TO_DEPOSIT_ACCOUNT">
                    Se devuelve a la cuenta de depósito
                  </option>
                </Select>
              </Field>
            </div>
          </FormCard>

          <FormCard
            title="Efectos de una compra en disputa"
            description="Mientras la disputa está abierta (utilización IN_DISPUTE)."
          >
            <Switch
              label="Congelar la causación de intereses"
              checked={draft.disputePolicy.freezeAccrual}
              onCheckedChange={(v) => {
                set(['disputePolicy', 'freezeAccrual'], v);
              }}
            />
            <Switch
              label="Excluir sus cuotas del pago mínimo"
              checked={draft.disputePolicy.excludeFromMinimum}
              onCheckedChange={(v) => {
                set(['disputePolicy', 'excludeFromMinimum'], v);
              }}
            />
            <Switch
              label="No contar mora ni reportar a centrales"
              checked={draft.disputePolicy.suspendDpd}
              onCheckedChange={(v) => {
                set(['disputePolicy', 'suspendDpd'], v);
              }}
            />
            <div className="grid gap-5 md:grid-cols-2">
              <IntField
                label="Duración máxima de la disputa"
                suffix="días"
                value={draft.disputePolicy.maxDays}
                onChange={(v) => {
                  set(['disputePolicy', 'maxDays'], v);
                }}
                error={error(['disputePolicy', 'maxDays'])}
              />
              <IntField
                label="Plazo para decidir"
                suffix="días hábiles"
                value={draft.disputePolicy.decisionDueBusinessDays}
                onChange={(v) => {
                  set(['disputePolicy', 'decisionDueBusinessDays'], v);
                }}
                error={error(['disputePolicy', 'decisionDueBusinessDays'])}
                hint="Vencimiento de la tarea del operador (y de la API de decisión)."
              />
            </div>
          </FormCard>
        </>
      )}
    </SettingSection>
  );
}

// ── Mora ──────────────────────────────────────────────────────────────────
const DELINQUENCY_LABELS: Readonly<Record<string, string>> = {
  blockPurchasesAtDpd: 'Bloquear compras desde',
  graceDays: 'Días de gracia',
  chargeMoratoriumInterest: 'Cobrar intereses moratorios',
  ratingBands: 'Tramos de calificación',
  'bureauReporting.enabled': 'Reporte a centrales',
  'bureauReporting.reportAtDpd': 'Reportar desde',
  'bureauReporting.priorNoticeDays': 'Aviso previo',
};

const RATING_TONE = [
  'bg-success',
  'bg-warning',
  'bg-violet-500',
  'bg-destructive',
  'bg-ink',
] as const;

export function DelinquencySection() {
  return (
    <SettingSection
      settingKey="delinquencyPolicy"
      title="Política de mora"
      description="Cuándo se bloquean las compras, cómo se califica la cartera por altura de mora y cuándo se reporta a centrales (simulado en el demo)."
      labels={DELINQUENCY_LABELS}
    >
      {({ draft, set, error }) => (
        <>
          <FormCard title="Compras y cobro">
            <div className="grid gap-5 md:grid-cols-2">
              <IntField
                label="Bloquear compras desde"
                suffix="días de mora"
                value={draft.blockPurchasesAtDpd}
                onChange={(v) => {
                  set(['blockPurchasesAtDpd'], v);
                }}
                error={error(['blockPurchasesAtDpd'])}
                hint="Con 1, cualquier cuota vencida bloquea nuevas compras (Hernán en el demo)."
              />
              <IntField
                label="Días de gracia"
                suffix="días"
                value={draft.graceDays}
                onChange={(v) => {
                  set(['graceDays'], v);
                }}
                error={error(['graceDays'])}
                hint="Antes de empezar a contar mora."
              />
            </div>
            <Switch
              label="Cobrar intereses moratorios"
              description="A la tasa moratoria del producto (máximo la usura vigente)."
              checked={draft.chargeMoratoriumInterest}
              onCheckedChange={(v) => {
                set(['chargeMoratoriumInterest'], v);
              }}
            />
          </FormCard>

          <FormCard
            title="Tramos de calificación"
            description="Continuos desde 0 días; el último queda abierto."
          >
            <div className="flex h-3 overflow-hidden rounded-full" aria-hidden="true">
              {draft.ratingBands.map((band, i) => (
                <span
                  key={band.rating}
                  className={cn(
                    RATING_TONE[i] ?? 'bg-slate-500',
                    'flex-1 border-r-2 border-card last:border-0',
                  )}
                />
              ))}
            </div>
            {error(['ratingBands']) && (
              <p role="alert" className="text-sm font-medium text-destructive">
                {error(['ratingBands'])}
              </p>
            )}
            <div className="grid gap-3 md:grid-cols-5">
              {draft.ratingBands.map((band, i) => (
                <div key={band.rating} className="rounded-xl border border-border p-3">
                  <p className="mb-2 flex items-center gap-2 font-display text-lg font-semibold">
                    <span className={cn('size-3 rounded-full', RATING_TONE[i] ?? 'bg-slate-500')} />{' '}
                    {band.rating}
                  </p>
                  <IntField
                    label="Desde"
                    suffix="días"
                    value={band.minDpd}
                    onChange={(v) => {
                      set(['ratingBands', i, 'minDpd'], v);
                    }}
                    error={error(['ratingBands', i, 'minDpd'])}
                  />
                  <div className="mt-3">
                    {band.maxDpd === null ? (
                      <p className="text-sm text-muted-foreground">en adelante</p>
                    ) : (
                      <IntField
                        label="Hasta"
                        suffix="días"
                        value={band.maxDpd}
                        onChange={(v) => {
                          set(['ratingBands', i, 'maxDpd'], v);
                        }}
                        error={error(['ratingBands', i, 'maxDpd'])}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </FormCard>

          <FormCard
            title="Reporte a centrales de riesgo"
            description="Simulado en el demo. Ley 1266: aviso previo al titular."
          >
            <Switch
              label="Reportar mora a centrales"
              checked={draft.bureauReporting.enabled}
              onCheckedChange={(v) => {
                set(['bureauReporting', 'enabled'], v);
              }}
            />
            <div
              className={cn(
                'grid gap-5 md:grid-cols-2',
                !draft.bureauReporting.enabled && 'opacity-50',
              )}
            >
              <IntField
                label="Reportar desde"
                suffix="días de mora"
                value={draft.bureauReporting.reportAtDpd}
                onChange={(v) => {
                  set(['bureauReporting', 'reportAtDpd'], v);
                }}
                error={error(['bureauReporting', 'reportAtDpd'])}
              />
              <IntField
                label="Aviso previo al cliente"
                suffix="días"
                value={draft.bureauReporting.priorNoticeDays}
                onChange={(v) => {
                  set(['bureauReporting', 'priorNoticeDays'], v);
                }}
                error={error(['bureauReporting', 'priorNoticeDays'])}
              />
            </div>
          </FormCard>
        </>
      )}
    </SettingSection>
  );
}
